import { Link } from "react-router-dom";
import { COMPANY } from "@/data/site";
import { useSeo } from "@/hooks/useSeo";

const H2 = ({ children }) => <h2 className="font-serif text-2xl md:text-3xl mt-12 mb-4 text-[var(--ink)]">{children}</h2>;
const P = ({ children }) => <p className="text-[var(--muted)] leading-relaxed mb-4">{children}</p>;
const LI = ({ children }) => <li className="text-[var(--muted)] leading-relaxed">{children}</li>;

export default function Privacy() {
  useSeo(
    "Privacy Policy | StoneCrest Exteriors",
    "How StoneCrest Exteriors Specialists Ltd collects, uses and protects your personal data under UK GDPR, including your rights and how to contact us.",
    "https://stonecrestexteriors.co.uk/privacy"
  );

  return (
    <div className="px-6 md:px-12 max-w-3xl mx-auto py-16 md:py-24" data-testid="privacy-page">
      <p className="text-[11px] uppercase tracking-[0.35em] text-[var(--oxblood)] mb-4">Legal</p>
      <h1 className="font-serif font-light text-5xl md:text-6xl leading-[1.02] tracking-tight text-[var(--ink)]">Privacy Policy</h1>
      <p className="mt-4 text-sm text-[var(--muted)]">Last updated: August 2026</p>

      <div className="mt-10">
        <H2>1. Who we are</H2>
        <P>This website is operated by <strong>StoneCrest Exteriors Specialists Ltd</strong>, trading as StoneCrest Exteriors ("we", "us", "our"). We provide exterior cleaning services to both residential and commercial customers. We are responsible for your personal data as the data controller. Our contact details are set out at the end of this policy.</P>

        <H2>2. Personal data we may collect</H2>
        <P>Depending on how you interact with us, we may collect:</P>
        <ul className="list-disc pl-6 space-y-1 mb-4">
          <LI>your name;</LI>
          <LI>your email address;</LI>
          <LI>your phone number;</LI>
          <LI>property, address and postcode details;</LI>
          <LI>the service(s) you are interested in;</LI>
          <LI>enquiry and quote information;</LI>
          <LI>messages and notes you send us;</LI>
          <LI>photographs or files you choose to upload, where applicable;</LI>
          <LI>website usage and technical information (such as pages visited), where applicable.</LI>
        </ul>

        <H2>3. How we collect information</H2>
        <ul className="list-disc pl-6 space-y-1 mb-4">
          <LI>through our contact forms;</LI>
          <LI>through our quote request forms;</LI>
          <LI>by email;</LI>
          <LI>by telephone;</LI>
          <LI>through your interactions with our website.</LI>
        </ul>

        <H2>4. Why we use personal data</H2>
        <ul className="list-disc pl-6 space-y-1 mb-4">
          <LI>to respond to your enquiries;</LI>
          <LI>to prepare quotations;</LI>
          <LI>to arrange and provide our services;</LI>
          <LI>to communicate with you;</LI>
          <LI>to manage our customer relationships;</LI>
          <LI>to operate and secure our website;</LI>
          <LI>to comply with our legal obligations.</LI>
        </ul>

        <H2>5. Our legal bases for processing</H2>
        <P>Under UK GDPR, we rely on the following legal bases:</P>
        <ul className="list-disc pl-6 space-y-1 mb-4">
          <LI><strong>Steps taken at your request before entering a contract</strong> — for example, responding to your enquiry and preparing a quotation.</LI>
          <LI><strong>Performance of a contract</strong> — where you have engaged us to carry out work.</LI>
          <LI><strong>Legitimate interests</strong> — to operate, improve and secure our business and website, where your interests and rights do not override those interests.</LI>
          <LI><strong>Legal obligations</strong> — where we are required to process data to comply with the law (for example accounting requirements).</LI>
          <LI><strong>Consent</strong> — where we rely on your consent (for example certain analytics cookies), which you may withdraw at any time.</LI>
        </ul>

        <H2>6. Service providers</H2>
        <P>We use trusted third-party service providers to help operate our website and manage communications with customers. This includes <strong>Brevo</strong>, which we use to send website enquiry and quote confirmation emails.</P>
        <P>These service providers may process personal information on our behalf only where necessary to provide their services to us and support the operation of our business.</P>

        <H2>7. Website analytics</H2>
        <P>We may use <strong>Google Analytics</strong> to understand how visitors use our website. Analytics is only used where you have consented to analytics cookies. You can change your preference at any time.</P>

        <H2>8. Cookies</H2>
        <P>For details of the cookies we use and how to manage them, please see our <Link to="/cookies" className="text-[var(--oxblood)] underline" data-testid="privacy-cookies-link">Cookie Policy</Link>.</P>

        <H2>9. Sharing your information</H2>
        <P>We may share your personal information only with service providers and processors necessary to operate our website and provide our services (such as our email provider), or where we are required to do so by law. <strong>We do not sell your personal data.</strong></P>

        <H2>10. Data retention</H2>
        <P>We keep your information only for as long as is reasonably necessary for the purposes described in this policy — including handling your enquiry or quote, providing our services, meeting legal and accounting requirements, and dealing with any disputes. When it is no longer needed, we will securely delete or anonymise it.</P>

        <H2>11. Data security</H2>
        <P>We use reasonable technical and organisational measures to protect your personal information against unauthorised access, loss, misuse or alteration.</P>

        <H2>12. Your rights under UK GDPR</H2>
        <P>You have the right to:</P>
        <ul className="list-disc pl-6 space-y-1 mb-4">
          <LI>access the personal data we hold about you;</LI>
          <LI>request correction of inaccurate data;</LI>
          <LI>request erasure of your data, where applicable;</LI>
          <LI>request restriction of processing;</LI>
          <LI>object to processing;</LI>
          <LI>request data portability, where applicable;</LI>
          <LI>withdraw consent at any time, where we rely on consent.</LI>
        </ul>

        <H2>13. Complaints</H2>
        <P>If you have any concerns about how we handle your personal data, please contact us first using the details below and we will do our best to help. You also have the right to complain to the UK Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[var(--oxblood)] underline">ico.org.uk</a>.</P>

        <H2>14. Contact details</H2>
        <div className="text-[var(--muted)] leading-relaxed mb-4">
          <p><strong className="text-[var(--ink)]">StoneCrest Exteriors Specialists Ltd</strong></p>
          <p>Registered in England and Wales</p>
          <p>Company No. 17390567</p>
          <p>Registered Office: The Exchange, Flat 8 Percy Street, Preston, PR1 1AW</p>
          <p>Email: <a href={`mailto:${COMPANY.email}`} className="text-[var(--oxblood)] underline">{COMPANY.email}</a></p>
          <p>Phone: <a href={COMPANY.phoneHref} className="text-[var(--oxblood)] underline">+44 7307 352758</a></p>
        </div>

        <H2>15. Changes to this policy</H2>
        <P>We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last updated" date above.</P>
      </div>
    </div>
  );
}
